import { create } from 'zustand';
import type { Comment } from '@/api/comments';
import { listComments } from '@/api/comments';

export interface CommentCount {
  total: number;
  unresolved: number;
}

export interface CommentState {
  /** All comments keyed by nodeId. */
  comments: Map<string, Comment[]>;
  /** Total number of comments across all nodes. */
  totalCount: number;
  /** Whether comments are currently loading. */
  isLoading: boolean;

  fetchComments: (mapId: string) => Promise<void>;
  addLocalComment: (comment: Comment) => void;
  updateLocalComment: (commentId: string, updates: Partial<Comment>) => void;
  removeLocalComment: (commentId: string) => void;
  clear: () => void;
}

function groupByNode(items: Comment[]): Map<string, Comment[]> {
  const map = new Map<string, Comment[]>();
  for (const c of items) {
    let arr = map.get(c.nodeId);
    if (!arr) {
      arr = [];
      map.set(c.nodeId, arr);
    }
    arr.push(c);
  }
  return map;
}

function countTotal(map: Map<string, Comment[]>): number {
  let n = 0;
  map.forEach((arr) => {
    n += arr.length;
  });
  return n;
}

export const useCommentStore = create<CommentState>((set, get) => ({
  comments: new Map(),
  totalCount: 0,
  isLoading: false,

  fetchComments: async (mapId: string) => {
    set({ isLoading: true });
    try {
      const result = await listComments(mapId);
      const grouped = groupByNode(result);
      set({ comments: grouped, totalCount: countTotal(grouped), isLoading: false });
    } catch (err) {
      console.error('[commentStore] Failed to fetch comments:', err);
      set({ isLoading: false });
    }
  },

  addLocalComment: (comment: Comment) => {
    const comments = new Map(get().comments);
    const arr = [...(comments.get(comment.nodeId) ?? []), comment];
    comments.set(comment.nodeId, arr);
    set({ comments, totalCount: countTotal(comments) });
  },

  updateLocalComment: (commentId: string, updates: Partial<Comment>) => {
    const comments = new Map(get().comments);
    let found = false;
    comments.forEach((arr, nodeId) => {
      if (found) return;
      const idx = arr.findIndex((c) => c.commentId === commentId);
      if (idx !== -1) {
        found = true;
        const updated = [...arr];
        updated[idx] = { ...updated[idx]!, ...updates };
        comments.set(nodeId, updated);
      }
    });
    if (found) {
      set({ comments, totalCount: countTotal(comments) });
    }
  },

  removeLocalComment: (commentId: string) => {
    const comments = new Map(get().comments);
    let found = false;
    comments.forEach((arr, nodeId) => {
      if (found) return;
      const idx = arr.findIndex((c) => c.commentId === commentId);
      if (idx !== -1) {
        found = true;
        const updated = arr.filter((c) => c.commentId !== commentId);
        if (updated.length === 0) {
          comments.delete(nodeId);
        } else {
          comments.set(nodeId, updated);
        }
      }
    });
    if (found) {
      set({ comments, totalCount: countTotal(comments) });
    }
  },

  clear: () => set({ comments: new Map(), totalCount: 0, isLoading: false }),
}));
